import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import MuscleMap, { buildMuscleImageDataUrl } from '../components/MuscleMap/MuscleMap';
import { MUSCLE_BY_ID } from '../components/MuscleMap/muscleDefinitions';

const DEFAULT_LINEART_FRONT_PATH = '/bodymap/front.png';
const DEFAULT_LINEART_BACK_PATH = '/bodymap/back.png';

const emptyMuscle = () => ({ id: null, name: '', imageUrl: '', muscleIds: [], sortOrder: 0, isActive: true });
const emptyEquipment = () => ({ id: null, name: '', imageUrl: '', sortOrder: 0, isActive: true });

const AdminExerciseTaxonomyPage = () => {
  const [loading, setLoading] = useState(true);

  const [muscles, setMuscles] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [muscleForm, setMuscleForm] = useState(emptyMuscle());
  const [equipmentForm, setEquipmentForm] = useState(emptyEquipment());

  const [showMuscleModal, setShowMuscleModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);

  const [muscleBodyView, setMuscleBodyView] = useState('front'); // front | back
  const [muscleBodyRegion, setMuscleBodyRegion] = useState('');
  const [muscleBodyMode, setMuscleBodyMode] = useState('silhouette'); // silhouette | image
  const [muscleBodyBaseFrontUrl, setMuscleBodyBaseFrontUrl] = useState('');
  const [muscleBodyBaseBackUrl, setMuscleBodyBaseBackUrl] = useState('');
  const [muscleBodyBaseFrontTransform, setMuscleBodyBaseFrontTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [muscleBodyBaseBackTransform, setMuscleBodyBaseBackTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [muscleBodyBaseOpacity, setMuscleBodyBaseOpacity] = useState(1);
  const [draggingBase, setDraggingBase] = useState(false);
  const dragStartRef = useRef(null);

  // Optional: exact line-art drawings (normal mode). These are embedded as data URLs so the generated SVG includes them.
  const [lineArtFrontDataUrl, setLineArtFrontDataUrl] = useState('');
  const [lineArtBackDataUrl, setLineArtBackDataUrl] = useState('');
  const [lineArtFrontTransform, setLineArtFrontTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [lineArtBackTransform, setLineArtBackTransform] = useState({ x: 0, y: 0, scale: 1 });

  const lineArtFrontInputRef = useRef(null);
  const lineArtBackInputRef = useRef(null);

  const [showAiImageModal, setShowAiImageModal] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiImageTarget, setAiImageTarget] = useState(null); // 'muscle' | 'equipment'
  const [aiImageQuery, setAiImageQuery] = useState('');
  const [aiImageSuggestions, setAiImageSuggestions] = useState([]);

  const muscleFileRef = useRef(null);
  const equipFileRef = useRef(null);

  const saveBodyMapTimerRef = useRef(null);

  const uploadToFolder = async (file, folder) => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/upload/image?folder=${encodeURIComponent(folder)}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.url;
  };

  const loadBodyBaseFromStorage = () => {
    try {
      const f = localStorage.getItem('exerciseTaxonomyBodyBaseFrontUrl') || '';
      const b = localStorage.getItem('exerciseTaxonomyBodyBaseBackUrl') || '';
      const tf = localStorage.getItem('exerciseTaxonomyBodyBaseFrontTransform') || '';
      const tb = localStorage.getItem('exerciseTaxonomyBodyBaseBackTransform') || '';
      const op = localStorage.getItem('exerciseTaxonomyBodyBaseOpacity') || '';
      setMuscleBodyBaseFrontUrl(f);
      setMuscleBodyBaseBackUrl(b);
      if (tf) {
        try {
          const p = JSON.parse(tf);
          setMuscleBodyBaseFrontTransform({ x: Number(p?.x || 0), y: Number(p?.y || 0), scale: Number(p?.scale || 1) || 1 });
        } catch {}
      }
      if (tb) {
        try {
          const p = JSON.parse(tb);
          setMuscleBodyBaseBackTransform({ x: Number(p?.x || 0), y: Number(p?.y || 0), scale: Number(p?.scale || 1) || 1 });
        } catch {}
      }

      if (op) {
        const n = Number(op);
        if (Number.isFinite(n)) setMuscleBodyBaseOpacity(Math.min(1, Math.max(0.15, n)));
      }
    } catch {}
  };

  const fetchAsDataUrl = async (path) => {
    try {
      const res = await fetch(path, { cache: 'no-cache' });
      if (!res.ok) return '';
      const blob = await res.blob();
      if (!String(blob.type || '').toLowerCase().startsWith('image/')) return '';
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(blob);
      });
      return String(dataUrl || '');
    } catch {
      return '';
    }
  };

  const persistBodyBaseToStorage = (frontUrl, backUrl) => {
    try {
      if (typeof frontUrl === 'string') localStorage.setItem('exerciseTaxonomyBodyBaseFrontUrl', frontUrl);
      if (typeof backUrl === 'string') localStorage.setItem('exerciseTaxonomyBodyBaseBackUrl', backUrl);
    } catch {}
  };

  const persistBodyTransformToStorage = (frontT, backT) => {
    try {
      if (frontT) localStorage.setItem('exerciseTaxonomyBodyBaseFrontTransform', JSON.stringify(frontT));
      if (backT) localStorage.setItem('exerciseTaxonomyBodyBaseBackTransform', JSON.stringify(backT));
    } catch {}
  };

  const persistLineArtTransformToStorage = (frontT, backT) => {
    try {
      if (frontT) localStorage.setItem('exerciseTaxonomyLineArtFrontTransform', JSON.stringify(frontT));
      if (backT) localStorage.setItem('exerciseTaxonomyLineArtBackTransform', JSON.stringify(backT));
    } catch {}
  };

  const loadLineArtTransformFromStorage = () => {
    try {
      const tf = localStorage.getItem('exerciseTaxonomyLineArtFrontTransform') || '';
      const tb = localStorage.getItem('exerciseTaxonomyLineArtBackTransform') || '';
      if (tf) {
        try {
          const p = JSON.parse(tf);
          setLineArtFrontTransform({ x: Number(p?.x || 0), y: Number(p?.y || 0), scale: Number(p?.scale || 1) || 1 });
        } catch {}
      }
      if (tb) {
        try {
          const p = JSON.parse(tb);
          setLineArtBackTransform({ x: Number(p?.x || 0), y: Number(p?.y || 0), scale: Number(p?.scale || 1) || 1 });
        } catch {}
      }
    } catch {}
  };

  const persistBodyOpacityToStorage = (opacity) => {
    try {
      localStorage.setItem('exerciseTaxonomyBodyBaseOpacity', String(opacity));
    } catch {}
  };

  const loadLineArtFromStorage = async () => {
    try {
      loadLineArtTransformFromStorage();
      const f = localStorage.getItem('exerciseTaxonomyLineArtFrontUrl') || '';
      const b = localStorage.getItem('exerciseTaxonomyLineArtBackUrl') || '';
      const [fd, bd] = await Promise.all([
        f ? fetchAsDataUrl(f) : Promise.resolve(''),
        b ? fetchAsDataUrl(b) : Promise.resolve(''),
      ]);
      if (fd) setLineArtFrontDataUrl(fd);
      if (bd) setLineArtBackDataUrl(bd);
    } catch {
      // ignore
    }
  };

  const persistLineArtToStorage = (frontUrl, backUrl) => {
    try {
      if (typeof frontUrl === 'string') localStorage.setItem('exerciseTaxonomyLineArtFrontUrl', frontUrl);
      if (typeof backUrl === 'string') localStorage.setItem('exerciseTaxonomyLineArtBackUrl', backUrl);
    } catch {}
  };

  const scheduleSaveBodyMapToServer = (next = {}) => {
    // Best-effort: persist to server so all admins/devices share the same base.
    if (saveBodyMapTimerRef.current) clearTimeout(saveBodyMapTimerRef.current);
    saveBodyMapTimerRef.current = setTimeout(async () => {
      try {
        const payload = {
          exerciseBodyMapFrontUrl: (next.frontUrl ?? muscleBodyBaseFrontUrl) ?? null,
          exerciseBodyMapBackUrl: (next.backUrl ?? muscleBodyBaseBackUrl) ?? null,
          exerciseBodyMapFrontX: (next.frontX ?? muscleBodyBaseFrontTransform.x),
          exerciseBodyMapFrontY: (next.frontY ?? muscleBodyBaseFrontTransform.y),
          exerciseBodyMapFrontScale: (next.frontScale ?? muscleBodyBaseFrontTransform.scale),
          exerciseBodyMapBackX: (next.backX ?? muscleBodyBaseBackTransform.x),
          exerciseBodyMapBackY: (next.backY ?? muscleBodyBaseBackTransform.y),
          exerciseBodyMapBackScale: (next.backScale ?? muscleBodyBaseBackTransform.scale),
          exerciseBodyMapOpacity: (next.opacity ?? muscleBodyBaseOpacity),
        };

        // normalize empty strings
        if (typeof payload.exerciseBodyMapFrontUrl === 'string' && !payload.exerciseBodyMapFrontUrl.trim()) payload.exerciseBodyMapFrontUrl = null;
        if (typeof payload.exerciseBodyMapBackUrl === 'string' && !payload.exerciseBodyMapBackUrl.trim()) payload.exerciseBodyMapBackUrl = null;

        await api.put('/gymsettings', payload);
      } catch {
        // ignore (non-blocking)
      }
    }, 650);
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const getActiveTransform = () => (muscleBodyView === 'front' ? muscleBodyBaseFrontTransform : muscleBodyBaseBackTransform);

  const getActiveLineArtTransform = () => (muscleBodyView === 'front' ? lineArtFrontTransform : lineArtBackTransform);

  const setActiveLineArtTransform = (next) => {
    if (muscleBodyView === 'front') {
      setLineArtFrontTransform(next);
      persistLineArtTransformToStorage(next, lineArtBackTransform);
    } else {
      setLineArtBackTransform(next);
      persistLineArtTransformToStorage(lineArtFrontTransform, next);
    }
  };

  const setActiveTransform = (next) => {
    if (muscleBodyView === 'front') {
      setMuscleBodyBaseFrontTransform(next);
      persistBodyTransformToStorage(next, muscleBodyBaseBackTransform);
    } else {
      setMuscleBodyBaseBackTransform(next);
      persistBodyTransformToStorage(muscleBodyBaseFrontTransform, next);
    }
  };

  const onBaseMouseDown = (e) => {
    const lineArtActive = muscleBodyMode === 'silhouette' && !!String((muscleBodyView === 'front' ? lineArtFrontDataUrl : lineArtBackDataUrl) || '').trim();
    const isImageMode = muscleBodyMode === 'image';
    if (!isImageMode && !lineArtActive) return;

    if (isImageMode) {
      const baseUrl = (muscleBodyView === 'front' ? muscleBodyBaseFrontUrl : muscleBodyBaseBackUrl) || '';
      if (!String(baseUrl).trim()) return;
    }

    setDraggingBase(true);
    const t = lineArtActive ? getActiveLineArtTransform() : getActiveTransform();
    dragStartRef.current = {
      kind: lineArtActive ? 'lineart' : 'image',
      x: e.clientX,
      y: e.clientY,
      startX: Number(t?.x || 0),
      startY: Number(t?.y || 0),
    };
  };

  const onBaseMouseMove = (e) => {
    if (!draggingBase || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    const kind = dragStartRef.current.kind || 'image';
    const t = kind === 'lineart' ? getActiveLineArtTransform() : getActiveTransform();
    const next = { ...t, x: dragStartRef.current.startX + dx, y: dragStartRef.current.startY + dy };
    if (kind === 'lineart') setActiveLineArtTransform(next);
    else setActiveTransform(next);
  };

  const onBaseMouseUp = () => {
    setDraggingBase(false);
    dragStartRef.current = null;
  };

  const onBaseWheel = (e) => {
    const lineArtActive = muscleBodyMode === 'silhouette' && !!String((muscleBodyView === 'front' ? lineArtFrontDataUrl : lineArtBackDataUrl) || '').trim();
    const isImageMode = muscleBodyMode === 'image';
    if (!isImageMode && !lineArtActive) return;

    if (isImageMode) {
      const baseUrl = (muscleBodyView === 'front' ? muscleBodyBaseFrontUrl : muscleBodyBaseBackUrl) || '';
      if (!String(baseUrl).trim()) return;
    }

    e.preventDefault();
    const t = lineArtActive ? getActiveLineArtTransform() : getActiveTransform();
    const scale = Number(t?.scale || 1) || 1;
    const delta = e.deltaY;
    const factor = delta > 0 ? 0.95 : 1.05;
    const nextScale = clamp(scale * factor, 0.4, 3);
    const next = { ...t, scale: Number(nextScale.toFixed(3)) };
    if (lineArtActive) setActiveLineArtTransform(next);
    else setActiveTransform(next);
  };

  const svgToDataUrl = (svg) => {
    const s = String(svg || '').trim();
    if (!s) return '';
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`;
  };

  const guessRegionByName = (name) => {
    const n = String(name || '').trim().toLowerCase();
    if (!n) return { view: 'front', region: '' };
    if (n.includes('peito')) return { view: 'front', region: 'chest' };
    if (n.includes('costas')) return { view: 'back', region: 'back' };
    if (n.includes('ombro')) return { view: 'front', region: 'shoulders' };
    if (n.includes('bíceps') || n.includes('biceps')) return { view: 'front', region: 'biceps' };
    if (n.includes('tríceps') || n.includes('triceps')) return { view: 'back', region: 'triceps' };
    if (n.includes('abd')) return { view: 'front', region: 'abs' };
    if (n.includes('glúte') || n.includes('glute')) return { view: 'back', region: 'glutes' };
    if (n.includes('géme') || n.includes('geme') || n.includes('panturr')) return { view: 'back', region: 'calves' };
    if (n.includes('perna') || n.includes('quadr') || n.includes('isqu') || n.includes('fem')) return { view: 'front', region: 'legs' };
    if (n.includes('corpo inteiro') || n.includes('full')) return { view: 'front', region: 'full' };
    return { view: 'front', region: '' };
  };

  const buildBodyMapSvg = useCallback((view, region) => {
    const selected = String(region || '');
    const lineArtDataUrl = view === 'back' ? String(lineArtBackDataUrl || '').trim() : String(lineArtFrontDataUrl || '').trim();
    const hasLineArt = !!lineArtDataUrl;
    const lt = view === 'back' ? (lineArtBackTransform || { x: 0, y: 0, scale: 1 }) : (lineArtFrontTransform || { x: 0, y: 0, scale: 1 });
    const ltx = Number(lt?.x || 0);
    const lty = Number(lt?.y || 0);
    const lts = Number(lt?.scale || 1) || 1;
    const lineArtTransform = `translate(${ltx} ${lty}) scale(${lts})`;
    const fillFor = (id) => {
      if (selected === 'full' && id !== 'outline') return hasLineArt ? 'rgba(239,68,68,0.40)' : '#ef4444';
      if (selected === id) return hasLineArt ? 'rgba(239,68,68,0.40)' : '#ef4444';
      // If we have the exact line-art, keep other regions invisible (hit-area comes from the path itself).
      if (hasLineArt) return 'transparent';
      return 'transparent';
    };

    // Default line-art "mold" — athletic male figure matching the reference anatomy chart (frente.jpg).
    // ViewBox 0 0 420 560.  Body centred x=210.
    // Broad shoulders, V-taper waist, thick muscular legs, arms hanging beside the body.
    // Head top ~y18, chin ~y82, shoulders y110-120, nipple ~y185, navel ~y270, hips y310, knees ~y410, feet ~y535.
    const outlineHumanFront = `
  <g>
    <!-- Head -->
    <path d="M190 52 C190 30, 230 30, 230 52 C232 62, 232 72, 226 80 C220 86, 200 86, 194 80 C188 72, 188 62, 190 52 Z" fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Neck -->
    <path d="M198 86 L194 106" fill="none" stroke="#0f172a" stroke-width="2"/>
    <path d="M222 86 L226 106" fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Torso outline — broad shoulders, V-taper to narrow waist -->
    <path d="M194 106 C178 108, 148 114, 118 124
             C100 132, 88 142, 84 154
             C82 162, 86 170, 94 176
             C106 184, 120 186, 138 188
             C150 190, 158 196, 164 208
             C170 224, 174 244, 176 262
             C178 280, 178 296, 176 310
             L244 310
             C242 296, 242 280, 244 262
             C246 244, 250 224, 256 208
             C262 196, 270 190, 282 188
             C300 186, 314 184, 326 176
             C334 170, 338 162, 336 154
             C332 142, 320 132, 302 124
             C272 114, 242 108, 226 106 Z"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Left arm outer -->
    <path d="M84 154 C74 168, 66 190, 62 214
             C58 240, 58 264, 62 286
             C66 306, 72 324, 78 340
             C82 352, 82 360, 78 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left arm inner -->
    <path d="M138 188 C128 200, 116 220, 108 242
             C100 264, 98 284, 100 304
             C102 320, 106 334, 110 346
             C112 354, 110 362, 106 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left hand -->
    <path d="M78 370 C74 378, 74 386, 80 390 C86 392, 96 390, 102 386 C106 382, 108 376, 106 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Right arm outer -->
    <path d="M336 154 C346 168, 354 190, 358 214
             C362 240, 362 264, 358 286
             C354 306, 348 324, 342 340
             C338 352, 338 360, 342 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right arm inner -->
    <path d="M282 188 C292 200, 304 220, 312 242
             C320 264, 322 284, 320 304
             C318 320, 314 334, 310 346
             C308 354, 310 362, 314 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right hand -->
    <path d="M342 370 C346 378, 346 386, 340 390 C334 392, 324 390, 318 386 C314 382, 312 376, 314 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Left leg outer -->
    <path d="M176 310 C170 322, 158 340, 148 362
             C138 386, 132 412, 134 436
             C136 456, 140 472, 142 486
             C142 498, 138 510, 130 522
             C126 530, 124 536, 128 540"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left leg inner -->
    <path d="M206 310 C206 324, 204 342, 200 362
             C196 384, 192 408, 192 430
             C192 450, 194 468, 196 484
             C196 496, 194 510, 188 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left foot -->
    <path d="M128 540 C136 542, 152 542, 162 538 C172 534, 178 528, 180 524 C182 520, 188 522, 188 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Right leg outer -->
    <path d="M244 310 C250 322, 262 340, 272 362
             C282 386, 288 412, 286 436
             C284 456, 280 472, 278 486
             C278 498, 282 510, 290 522
             C294 530, 296 536, 292 540"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right leg inner -->
    <path d="M214 310 C214 324, 216 342, 220 362
             C224 384, 228 408, 228 430
             C228 450, 226 468, 224 484
             C224 496, 226 510, 232 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right foot -->
    <path d="M292 540 C284 542, 268 542, 258 538 C248 534, 242 528, 240 524 C238 520, 232 522, 232 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- FRONT MUSCLE DETAIL LINES -->
    <g stroke="#0f172a" stroke-width="1.2" fill="none" stroke-linecap="round" stroke-linejoin="round">
      <!-- Collarbones / trapezius -->
      <path d="M194 110 C200 106, 206 104, 210 106"/>
      <path d="M226 110 C220 106, 214 104, 210 106"/>
      <!-- Center line (linea alba) -->
      <path d="M210 118 L210 308"/>
      <!-- Pec upper border (curved across chest) -->
      <path d="M140 188 C160 168, 190 158, 210 158 C230 158, 260 168, 280 188"/>
      <!-- Pec lower border -->
      <path d="M150 206 C170 218, 194 224, 210 224 C226 224, 250 218, 270 206"/>
      <!-- Pec-deltoid separation -->
      <path d="M140 188 C144 194, 148 200, 150 206"/>
      <path d="M280 188 C276 194, 272 200, 270 206"/>
      <!-- Deltoid caps (shoulder muscle round) -->
      <path d="M86 154 C88 166, 92 178, 98 186"/>
      <path d="M334 154 C332 166, 328 178, 322 186"/>
      <!-- Serratus anterior (rib-side lines) left -->
      <path d="M154 210 C150 218, 146 228, 144 236"/>
      <path d="M156 222 C152 230, 148 240, 146 248"/>
      <path d="M158 234 C154 242, 150 252, 148 260"/>
      <!-- Serratus right -->
      <path d="M266 210 C270 218, 274 228, 276 236"/>
      <path d="M264 222 C268 230, 272 240, 274 248"/>
      <path d="M262 234 C266 242, 270 252, 272 260"/>
      <!-- Abs horizontal lines -->
      <path d="M192 240 C200 236, 220 236, 228 240"/>
      <path d="M190 258 C200 254, 220 254, 230 258"/>
      <path d="M188 276 C200 272, 220 272, 232 276"/>
      <path d="M188 294 C200 290, 220 290, 232 294"/>
      <!-- Abs vertical -->
      <path d="M200 232 L200 304"/>
      <path d="M220 232 L220 304"/>
      <!-- Oblique lines -->
      <path d="M158 220 C156 246, 158 272, 164 296 C168 306, 172 312, 176 316"/>
      <path d="M262 220 C264 246, 262 272, 256 296 C252 306, 248 312, 244 316"/>
      <!-- V-cut (inguinal crease) -->
      <path d="M176 306 C192 296, 210 292, 210 292 C210 292, 228 296, 244 306"/>
      <!-- Bicep line (arm separation) -->
      <path d="M96 198 C100 218, 104 238, 106 256"/>
      <path d="M324 198 C320 218, 316 238, 314 256"/>
      <!-- Forearm line -->
      <path d="M86 286 C90 304, 96 320, 100 334"/>
      <path d="M334 286 C330 304, 324 320, 320 334"/>
      <!-- Quad outer sweep -->
      <path d="M174 316 C166 340, 156 368, 148 396 C142 420, 140 444, 142 464"/>
      <path d="M246 316 C254 340, 264 368, 272 396 C278 420, 280 444, 278 464"/>
      <!-- Quad inner (tear-drop) -->
      <path d="M206 316 C204 340, 200 368, 196 396 C194 416, 194 436, 196 454"/>
      <path d="M214 316 C216 340, 220 368, 224 396 C226 416, 226 436, 224 454"/>
      <!-- Rectus femoris (center quad) -->
      <path d="M172 340 C186 330, 198 326, 206 328"/>
      <path d="M248 340 C234 330, 222 326, 214 328"/>
      <!-- Knee caps -->
      <path d="M140 468 C154 458, 174 454, 196 460"/>
      <path d="M280 468 C266 458, 246 454, 224 460"/>
      <!-- Shin / calf front -->
      <path d="M138 488 C140 502, 140 516, 136 528"/>
      <path d="M282 488 C280 502, 280 516, 284 528"/>
    </g>
  </g>
`;

    const outlineHumanBack = `
  <g>
    <!-- Head -->
    <path d="M190 52 C190 30, 230 30, 230 52 C232 62, 232 72, 226 80 C220 86, 200 86, 194 80 C188 72, 188 62, 190 52 Z" fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Neck -->
    <path d="M198 86 L194 106" fill="none" stroke="#0f172a" stroke-width="2"/>
    <path d="M222 86 L226 106" fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Torso outline (same as front) -->
    <path d="M194 106 C178 108, 148 114, 118 124
             C100 132, 88 142, 84 154
             C82 162, 86 170, 94 176
             C106 184, 120 186, 138 188
             C150 190, 158 196, 164 208
             C170 224, 174 244, 176 262
             C178 280, 178 296, 176 310
             L244 310
             C242 296, 242 280, 244 262
             C246 244, 250 224, 256 208
             C262 196, 270 190, 282 188
             C300 186, 314 184, 326 176
             C334 170, 338 162, 336 154
             C332 142, 320 132, 302 124
             C272 114, 242 108, 226 106 Z"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Left arm outer -->
    <path d="M84 154 C74 168, 66 190, 62 214
             C58 240, 58 264, 62 286
             C66 306, 72 324, 78 340
             C82 352, 82 360, 78 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left arm inner -->
    <path d="M138 188 C128 200, 116 220, 108 242
             C100 264, 98 284, 100 304
             C102 320, 106 334, 110 346
             C112 354, 110 362, 106 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left hand -->
    <path d="M78 370 C74 378, 74 386, 80 390 C86 392, 96 390, 102 386 C106 382, 108 376, 106 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Right arm outer -->
    <path d="M336 154 C346 168, 354 190, 358 214
             C362 240, 362 264, 358 286
             C354 306, 348 324, 342 340
             C338 352, 338 360, 342 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right arm inner -->
    <path d="M282 188 C292 200, 304 220, 312 242
             C320 264, 322 284, 320 304
             C318 320, 314 334, 310 346
             C308 354, 310 362, 314 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right hand -->
    <path d="M342 370 C346 378, 346 386, 340 390 C334 392, 324 390, 318 386 C314 382, 312 376, 314 370"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Left leg outer -->
    <path d="M176 310 C170 322, 158 340, 148 362
             C138 386, 132 412, 134 436
             C136 456, 140 472, 142 486
             C142 498, 138 510, 130 522
             C126 530, 124 536, 128 540"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left leg inner -->
    <path d="M206 310 C206 324, 204 342, 200 362
             C196 384, 192 408, 192 430
             C192 450, 194 468, 196 484
             C196 496, 194 510, 188 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Left foot -->
    <path d="M128 540 C136 542, 152 542, 162 538 C172 534, 178 528, 180 524 C182 520, 188 522, 188 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- Right leg outer -->
    <path d="M244 310 C250 322, 262 340, 272 362
             C282 386, 288 412, 286 436
             C284 456, 280 472, 278 486
             C278 498, 282 510, 290 522
             C294 530, 296 536, 292 540"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right leg inner -->
    <path d="M214 310 C214 324, 216 342, 220 362
             C224 384, 228 408, 228 430
             C228 450, 226 468, 224 484
             C224 496, 226 510, 232 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>
    <!-- Right foot -->
    <path d="M292 540 C284 542, 268 542, 258 538 C248 534, 242 528, 240 524 C238 520, 232 522, 232 522"
      fill="none" stroke="#0f172a" stroke-width="2"/>

    <!-- BACK MUSCLE DETAIL LINES -->
    <g stroke="#0f172a" stroke-width="1.2" fill="none" stroke-linecap="round" stroke-linejoin="round">
      <!-- Spine -->
      <path d="M210 106 L210 310"/>
      <!-- Trapezius upper -->
      <path d="M194 110 C200 104, 206 102, 210 106"/>
      <path d="M226 110 C220 104, 214 102, 210 106"/>
      <!-- Trapezius to shoulder -->
      <path d="M138 188 C160 164, 192 154, 210 154"/>
      <path d="M282 188 C260 164, 228 154, 210 154"/>
      <!-- Infraspinatus -->
      <path d="M148 204 C172 192, 198 188, 210 194"/>
      <path d="M272 204 C248 192, 222 188, 210 194"/>
      <!-- Teres / lat border -->
      <path d="M146 222 C170 210, 198 208, 210 214"/>
      <path d="M274 222 C250 210, 222 208, 210 214"/>
      <!-- Lat V-shape -->
      <path d="M144 228 C150 254, 156 278, 164 296 C168 306, 172 312, 176 316"/>
      <path d="M276 228 C270 254, 264 278, 256 296 C252 306, 248 312, 244 316"/>
      <!-- Erector spinae -->
      <path d="M202 216 C200 248, 200 278, 202 298 C204 306, 206 312, 208 316"/>
      <path d="M218 216 C220 248, 220 278, 218 298 C216 306, 214 312, 212 316"/>
      <!-- Lower back horizontal -->
      <path d="M166 286 C188 276, 210 272, 210 272 C210 272, 232 276, 254 286"/>
      <!-- Rear deltoids -->
      <path d="M86 154 C88 166, 92 178, 98 186"/>
      <path d="M334 154 C332 166, 328 178, 322 186"/>
      <!-- Tricep lines -->
      <path d="M92 202 C98 222, 102 242, 104 260"/>
      <path d="M328 202 C322 222, 318 242, 316 260"/>
      <path d="M84 280 C88 298, 94 316, 98 330"/>
      <path d="M336 280 C332 298, 326 316, 322 330"/>
      <!-- Glute line -->
      <path d="M176 306 C192 296, 228 296, 244 306"/>
      <!-- Glute separation -->
      <path d="M210 300 L210 342"/>
      <!-- Hamstring outer -->
      <path d="M172 322 C164 346, 154 376, 148 406 C142 432, 142 456, 146 474"/>
      <path d="M248 322 C256 346, 266 376, 272 406 C278 432, 278 456, 274 474"/>
      <!-- Hamstring inner -->
      <path d="M206 320 C204 348, 200 380, 198 412 C196 438, 196 460, 198 478"/>
      <path d="M214 320 C216 348, 220 380, 222 412 C224 438, 224 460, 222 478"/>
      <!-- Calf muscles -->
      <path d="M140 486 C138 502, 136 516, 134 528"/>
      <path d="M280 486 C282 502, 284 516, 286 528"/>
      <!-- Forearm back -->
      <path d="M78 342 C82 354, 84 364, 82 372"/>
      <path d="M342 342 C338 354, 336 364, 338 372"/>
    </g>
  </g>
`;

    const base = `
  <rect width="420" height="560" rx="18" fill="#ffffff"/>
  ${hasLineArt ? `<image href="${lineArtDataUrl}" x="0" y="0" width="420" height="560" preserveAspectRatio="none" transform="${lineArtTransform}"/>` : ''}
`;

    const silhouetteFront = hasLineArt ? '' : outlineHumanFront;

    const silhouetteBack = hasLineArt ? '' : outlineHumanBack;

    const baseImageUrl = view === 'back'
      ? String(muscleBodyBaseBackUrl || '').trim()
      : String(muscleBodyBaseFrontUrl || '').trim();

    const t = view === 'back' ? muscleBodyBaseBackTransform : muscleBodyBaseFrontTransform;
    const tx = Number(t?.x || 0);
    const ty = Number(t?.y || 0);
    const ts = Number(t?.scale || 1) || 1;
    const imageTransform = `translate(${tx} ${ty}) scale(${ts})`;

    const shouldUseImage = muscleBodyMode === 'image' && !!baseImageUrl;

    if (shouldUseImage && view === 'back') {
      return `
<svg xmlns="http://www.w3.org/2000/svg" width="420" height="560" viewBox="0 0 420 560">
  <rect width="420" height="560" rx="18" fill="#ffffff"/>
  <image href="${baseImageUrl}" x="0" y="0" width="420" height="560" preserveAspectRatio="none" transform="${imageTransform}" opacity="${muscleBodyBaseOpacity}"/>

  <g stroke="none">
    <path d="M165 185 C 175 155, 245 155, 255 185 C 270 230, 275 280, 265 335 C 255 365, 165 365, 155 335 C 145 280, 150 230, 165 185 Z" fill="${selected === 'full' || selected === 'back' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M105 210 C 95 245, 95 285, 110 320 C 130 330, 142 300, 140 265 C 138 235, 125 215, 105 210 Z" fill="${selected === 'full' || selected === 'triceps' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M315 210 C 325 245, 325 285, 310 320 C 290 330, 278 300, 280 265 C 282 235, 295 215, 315 210 Z" fill="${selected === 'full' || selected === 'triceps' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M165 345 C 175 330, 195 325, 210 325 C 225 325, 245 330, 255 345 C 260 370, 245 395, 210 395 C 175 395, 160 370, 165 345 Z" fill="${selected === 'full' || selected === 'glutes' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M175 435 C 160 465, 160 505, 175 530 C 195 535, 205 510, 205 480 C 205 455, 195 440, 175 435 Z" fill="${selected === 'full' || selected === 'calves' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M245 435 C 260 465, 260 505, 245 530 C 225 535, 215 510, 215 480 C 215 455, 225 440, 245 435 Z" fill="${selected === 'full' || selected === 'calves' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M165 385 C 175 365, 195 360, 210 360 C 225 360, 245 365, 255 385 C 270 415, 270 455, 255 485 C 245 505, 225 530, 210 540 C 195 530, 175 505, 165 485 C 150 455, 150 415, 165 385 Z" fill="${selected === 'full' || selected === 'legs' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
  </g>

  <text x="210" y="535" text-anchor="middle" font-family="Arial" font-size="14" fill="#64748b">Back view</text>
</svg>`;
    }

    if (shouldUseImage && view !== 'back') {
      return `
<svg xmlns="http://www.w3.org/2000/svg" width="420" height="560" viewBox="0 0 420 560">
  <rect width="420" height="560" rx="18" fill="#ffffff"/>
  <image href="${baseImageUrl}" x="0" y="0" width="420" height="560" preserveAspectRatio="none" transform="${imageTransform}" opacity="${muscleBodyBaseOpacity}"/>

  <g stroke="none">
    <path d="M140 170 C 150 145, 175 135, 195 150 C 190 175, 165 190, 145 190 C 135 190, 135 180, 140 170 Z" fill="${selected === 'full' || selected === 'shoulders' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M280 170 C 270 145, 245 135, 225 150 C 230 175, 255 190, 275 190 C 285 190, 285 180, 280 170 Z" fill="${selected === 'full' || selected === 'shoulders' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M165 190 C 175 165, 245 165, 255 190 C 260 215, 245 235, 210 235 C 175 235, 160 215, 165 190 Z" fill="${selected === 'full' || selected === 'chest' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M110 210 C 95 240, 95 285, 115 310 C 140 305, 145 260, 130 225 C 125 215, 120 210, 110 210 Z" fill="${selected === 'full' || selected === 'biceps' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M310 210 C 325 240, 325 285, 305 310 C 280 305, 275 260, 290 225 C 295 215, 300 210, 310 210 Z" fill="${selected === 'full' || selected === 'biceps' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M185 235 C 190 220, 230 220, 235 235 C 245 270, 245 320, 235 350 C 230 365, 190 365, 185 350 C 175 320, 175 270, 185 235 Z" fill="${selected === 'full' || selected === 'abs' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M170 360 C 185 345, 205 345, 215 360 C 225 400, 225 445, 215 490 C 210 510, 195 530, 180 540 C 170 530, 160 505, 155 485 C 145 440, 150 395, 170 360 Z" fill="${selected === 'full' || selected === 'legs' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
    <path d="M250 360 C 235 345, 215 345, 205 360 C 195 400, 195 445, 205 490 C 210 510, 225 530, 240 540 C 250 530, 260 505, 265 485 C 275 440, 270 395, 250 360 Z" fill="${selected === 'full' || selected === 'legs' ? 'rgba(239,68,68,0.35)' : 'transparent'}"/>
  </g>

  <text x="210" y="535" text-anchor="middle" font-family="Arial" font-size="14" fill="#64748b">Front view</text>
</svg>`;
    }

    // Clickable body-map with regions matching the new multi-part body.
    if (view === 'back') {
      return `
<svg xmlns="http://www.w3.org/2000/svg" width="420" height="560" viewBox="0 0 420 560">
  ${base}
  ${silhouetteBack}

  <g stroke="none">
    <!-- Back (upper + lower back) -->
    <path id="back" d="M150 140 C162 124, 258 124, 270 140 C282 180, 282 240, 270 290 C258 310, 162 310, 150 290 C138 240, 138 180, 150 140 Z" fill="${fillFor('back')}"/>
    <!-- Triceps -->
    <path id="triceps" d="M70 180 C62 210, 60 252, 66 290 C72 312, 100 316, 104 286 C106 254, 96 216, 86 192 C80 180, 74 176, 70 180 Z" fill="${fillFor('triceps')}"/>
    <path d="M350 180 C358 210, 360 252, 354 290 C348 312, 320 316, 316 286 C314 254, 324 216, 334 192 C340 180, 346 176, 350 180 Z" fill="${fillFor('triceps')}"/>
    <!-- Glutes -->
    <path id="glutes" d="M178 300 C192 288, 228 288, 242 300 C256 324, 248 348, 210 348 C172 348, 164 324, 178 300 Z" fill="${fillFor('glutes')}"/>
    <!-- Calves -->
    <path id="calves" d="M136 478 C132 498, 130 516, 134 530 C146 536, 162 524, 166 504 C168 490, 156 480, 136 478 Z" fill="${fillFor('calves')}"/>
    <path d="M284 478 C288 498, 290 516, 286 530 C274 536, 258 524, 254 504 C252 490, 264 480, 284 478 Z" fill="${fillFor('calves')}"/>
    <!-- Legs (hamstrings) -->
    <path id="legs" d="M178 338 C190 322, 230 322, 242 338 C258 368, 282 420, 282 460 C282 490, 268 516, 250 530 C232 540, 210 542, 190 540 C170 534, 148 510, 138 480 C130 450, 136 400, 148 368 C158 348, 168 340, 178 338 Z" fill="${fillFor('legs')}"/>
  </g>

  <text x="210" y="548" text-anchor="middle" font-family="Arial" font-size="12" fill="#64748b">Back view</text>
</svg>`;
    }

    // front
    return `
<svg xmlns="http://www.w3.org/2000/svg" width="420" height="560" viewBox="0 0 420 560">
  ${base}
  ${silhouetteFront}

  <g stroke="none">
    <!-- Shoulders / Delts -->
    <path id="shoulders" d="M88 142 C80 156, 78 172, 84 184 C96 190, 108 180, 108 164 C108 152, 100 144, 88 142 Z" fill="${fillFor('shoulders')}"/>
    <path d="M332 142 C340 156, 342 172, 336 184 C324 190, 312 180, 312 164 C312 152, 320 144, 332 142 Z" fill="${fillFor('shoulders')}"/>
    <!-- Chest / Pecs -->
    <path id="chest" d="M148 154 C160 138, 260 138, 272 154 C280 178, 264 214, 210 222 C156 214, 140 178, 148 154 Z" fill="${fillFor('chest')}"/>
    <!-- Biceps -->
    <path id="biceps" d="M72 190 C62 220, 60 260, 68 296 C76 316, 102 316, 106 288 C108 258, 98 220, 88 198 C82 188, 76 186, 72 190 Z" fill="${fillFor('biceps')}"/>
    <path d="M348 190 C358 220, 360 260, 352 296 C344 316, 318 316, 314 288 C312 258, 322 220, 332 198 C338 188, 344 186, 348 190 Z" fill="${fillFor('biceps')}"/>
    <!-- Abs -->
    <path id="abs" d="M186 226 C192 214, 228 214, 234 226 C244 258, 244 288, 234 308 C228 316, 192 316, 186 308 C176 288, 176 258, 186 226 Z" fill="${fillFor('abs')}"/>
    <!-- Legs / Quads -->
    <path id="legs" d="M176 316 C190 304, 206 304, 210 312 C210 350, 196 404, 190 446 C186 472, 180 504, 172 528 C156 536, 136 510, 134 480 C132 448, 140 400, 152 362 C160 340, 168 326, 176 316 Z" fill="${fillFor('legs')}"/>
    <path d="M244 316 C230 304, 214 304, 210 312 C210 350, 224 404, 230 446 C234 472, 240 504, 248 528 C264 536, 284 510, 286 480 C288 448, 280 400, 268 362 C260 340, 252 326, 244 316 Z" fill="${fillFor('legs')}"/>
  </g>

  <text x="210" y="548" text-anchor="middle" font-family="Arial" font-size="12" fill="#64748b">Front view</text>
</svg>`;
  }, [muscleBodyMode, muscleBodyBaseFrontUrl, muscleBodyBaseBackUrl, muscleBodyBaseFrontTransform, muscleBodyBaseBackTransform, muscleBodyBaseOpacity, lineArtFrontDataUrl, lineArtBackDataUrl, lineArtFrontTransform, lineArtBackTransform]);

  const bodyMapSvg = useMemo(() => buildBodyMapSvg(muscleBodyView, muscleBodyRegion), [buildBodyMapSvg, muscleBodyView, muscleBodyRegion]);
  const bodyMapUrl = useMemo(() => svgToDataUrl(bodyMapSvg), [bodyMapSvg]);

  // MuscleMap: multi-seleção com toggle (fonte de verdade = muscleIds).
  const onMuscleToggle = useCallback((m) => {
    setMuscleForm(prev => {
      const id = m?.muscleId;
      if (!id) return prev;
      const cur = Array.isArray(prev.muscleIds) ? prev.muscleIds : [];
      return { ...prev, muscleIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  }, []);
  const selectedMuscleNames = (muscleForm.muscleIds || []).map(id => MUSCLE_BY_ID[id]?.name || id);

  const unwrapAiProxyUrl = (rawUrl) => {
    const u = String(rawUrl || '').trim();
    if (!u) return '';
    // If backend returns our proxy endpoint, browsers may load the direct URL instead.
    if (u.includes('/api/billing/articles/ai/image-proxy')) {
      try {
        const parsed = new URL(u, window.location.origin);
        const inner = parsed.searchParams.get('url');
        if (inner) return inner;
      } catch {}
    }
    return u;
  };

  const pickAiImageUrls = (img) => {
    const full = unwrapAiProxyUrl(img?.url || img?.proxyUrl || '');
    const thumb = unwrapAiProxyUrl(img?.thumbUrl || img?.proxyThumbUrl || full);
    return { full, thumb };
  };

  const openAiImageSearch = async (target) => {
    const isMuscle = target === 'muscle';
    const name = String(isMuscle ? muscleForm.name : equipmentForm.name).trim();
    if (!name) {
      toast.error('Primeiro preenche o nome');
      return;
    }

    setAiImageTarget(target);
    setAiImageSuggestions([]);
    setAiImageQuery(name);
    setShowAiImageModal(true);

    try {
      setAiImageLoading(true);
      const payload = {
        query: name,
        // reusing the same backend criteria as billing/articles
        family: isMuscle ? 'muscle group' : 'gym equipment',
        brand: 'fitness',
      };
      const res = await api.post('/billing/articles/ai/image-suggestions', payload);
      setAiImageQuery(res.data?.suggestedQuery || name);
      setAiImageSuggestions(Array.isArray(res.data?.images) ? res.data.images : []);
    } catch {
      toast.error('Não foi possível procurar imagens');
      setAiImageSuggestions([]);
    } finally {
      setAiImageLoading(false);
    }
  };

  const applyAiImage = (url) => {
    const u = String(url || '').trim();
    if (!u) return;
    if (aiImageTarget === 'muscle') {
      setMuscleForm(prev => ({ ...prev, imageUrl: u }));
    } else if (aiImageTarget === 'equipment') {
      setEquipmentForm(prev => ({ ...prev, imageUrl: u }));
    }
    setShowAiImageModal(false);
  };

  const loadAll = async () => {
    try {
      const [mRes, eRes] = await Promise.all([
        api.get('/exercise-taxonomy/muscle-groups', { params: { activeOnly: false } }),
        api.get('/exercise-taxonomy/equipment-types', { params: { activeOnly: false } }),
      ]);
      setMuscles(Array.isArray(mRes.data) ? mRes.data : []);
      setEquipment(Array.isArray(eRes.data) ? eRes.data : []);
    } catch (e) {
      toast.error('Erro ao carregar músculos/equipamentos');
      setMuscles([]);
      setEquipment([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    // Load defaults from server and fallback to localStorage
    const loadFromServer = async () => {
      try {
        const res = await api.get('/gymsettings');
        const s = res.data || {};
        if (s.exerciseBodyMapFrontUrl) setMuscleBodyBaseFrontUrl(s.exerciseBodyMapFrontUrl);
        if (s.exerciseBodyMapBackUrl) setMuscleBodyBaseBackUrl(s.exerciseBodyMapBackUrl);
        if (typeof s.exerciseBodyMapFrontX === 'number' || typeof s.exerciseBodyMapFrontY === 'number' || typeof s.exerciseBodyMapFrontScale === 'number') {
          setMuscleBodyBaseFrontTransform({
            x: Number(s.exerciseBodyMapFrontX || 0),
            y: Number(s.exerciseBodyMapFrontY || 0),
            scale: Number(s.exerciseBodyMapFrontScale || 1) || 1,
          });
        }
        if (typeof s.exerciseBodyMapBackX === 'number' || typeof s.exerciseBodyMapBackY === 'number' || typeof s.exerciseBodyMapBackScale === 'number') {
          setMuscleBodyBaseBackTransform({
            x: Number(s.exerciseBodyMapBackX || 0),
            y: Number(s.exerciseBodyMapBackY || 0),
            scale: Number(s.exerciseBodyMapBackScale || 1) || 1,
          });
        }
        if (typeof s.exerciseBodyMapOpacity === 'number') {
          const v = Math.min(1, Math.max(0.15, Number(s.exerciseBodyMapOpacity)));
          setMuscleBodyBaseOpacity(v);
        }

        // keep local fallback values if server empty
        if (!s.exerciseBodyMapFrontUrl && !s.exerciseBodyMapBackUrl) loadBodyBaseFromStorage();
      } catch {
        loadBodyBaseFromStorage();
      }
    };
    loadFromServer();
  }, []);

  useEffect(() => {
    // Load optional exact line-art drawings for NORMAL mode.
    // Put the files in ClientApp/public/bodymap/front.png and back.png.
    const loadLineArt = async () => {
      // 1) Prefer localStorage (user uploaded once in the UI)
      await loadLineArtFromStorage();

      // 2) Fallback to static public files
      const [f, b] = await Promise.all([
        fetchAsDataUrl(DEFAULT_LINEART_FRONT_PATH),
        fetchAsDataUrl(DEFAULT_LINEART_BACK_PATH),
      ]);
      if (f && !String(lineArtFrontDataUrl || '').trim()) setLineArtFrontDataUrl(f);
      if (b && !String(lineArtBackDataUrl || '').trim()) setLineArtBackDataUrl(b);
    };
    loadLineArt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMuscleModal = (nextForm) => {
    const f = nextForm || emptyMuscle();
    setMuscleForm(f);

    // Only apply localStorage defaults if we don't already have server defaults.
    if (!muscleBodyBaseFrontUrl && !muscleBodyBaseBackUrl) {
      loadBodyBaseFromStorage();
    }

    const firstId = (f?.muscleIds || [])[0];
    const canonical = firstId ? MUSCLE_BY_ID[firstId] : null;
    const guess = guessRegionByName(f?.name);
    setMuscleBodyView(canonical ? canonical.view : (guess.view || 'front'));
    setMuscleBodyRegion(guess.region || '');
    setShowMuscleModal(true);
  };

  const saveMuscle = async () => {
    const name = (muscleForm.name || '').trim();
    if (!name) {
      toast.error('Nome é obrigatório');
      return;
    }

    const dto = {
      name,
      imageUrl: (muscleForm.imageUrl || '').trim() || null,
      muscleId: ((muscleForm.muscleIds || []).join(',')) || null,
      sortOrder: Number(muscleForm.sortOrder || 0),
      isActive: !!muscleForm.isActive,
    };

    try {
      if (muscleForm.id) {
        await api.put(`/exercise-taxonomy/muscle-groups/${muscleForm.id}`, { ...dto, id: muscleForm.id });
        toast.success('Músculo atualizado ✅');
      } else {
        await api.post('/exercise-taxonomy/muscle-groups', dto);
        toast.success('Músculo criado ✅');
      }
      setShowMuscleModal(false);
      setMuscleForm(emptyMuscle());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erro ao guardar músculo');
    }
  };

  const saveEquipment = async () => {
    const name = (equipmentForm.name || '').trim();
    if (!name) {
      toast.error('Nome é obrigatório');
      return;
    }

    const dto = {
      name,
      imageUrl: (equipmentForm.imageUrl || '').trim() || null,
      sortOrder: Number(equipmentForm.sortOrder || 0),
      isActive: !!equipmentForm.isActive,
    };

    try {
      if (equipmentForm.id) {
        await api.put(`/exercise-taxonomy/equipment-types/${equipmentForm.id}`, { ...dto, id: equipmentForm.id });
        toast.success('Equipamento atualizado ✅');
      } else {
        await api.post('/exercise-taxonomy/equipment-types', dto);
        toast.success('Equipamento criado ✅');
      }
      setShowEquipmentModal(false);
      setEquipmentForm(emptyEquipment());
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erro ao guardar equipamento');
    }
  };

  const deleteMuscle = async (id) => {
    if (!window.confirm('Eliminar este músculo?')) return;
    try {
      await api.delete(`/exercise-taxonomy/muscle-groups/${id}`);
      toast.success('Eliminado ✅');
      loadAll();
    } catch {
      toast.error('Erro ao eliminar');
    }
  };

  const deleteEquipment = async (id) => {
    if (!window.confirm('Eliminar este equipamento?')) return;
    try {
      await api.delete(`/exercise-taxonomy/equipment-types/${id}`);
      toast.success('Eliminado ✅');
      loadAll();
    } catch {
      toast.error('Erro ao eliminar');
    }
  };

  const sortedMuscles = useMemo(() => {
    return [...(muscles || [])].sort((a, b) => (a.sortOrder - b.sortOrder) || String(a.name).localeCompare(String(b.name)));
  }, [muscles]);

  const sortedEquipment = useMemo(() => {
    return [...(equipment || [])].sort((a, b) => (a.sortOrder - b.sortOrder) || String(a.name).localeCompare(String(b.name)));
  }, [equipment]);

  if (loading) {
    return <div style={{ padding: 24, color: '#6b7280' }}>⏳ A carregar…</div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>🏷️ Taxonomia de Exercícios</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>Cria/edita os valores dos combobox: Músculos e Equipamentos (com imagem).</div>
        </div>
        <button onClick={loadAll} style={secondaryBtn}>🔄 Atualizar</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' }}>
        {/* Muscle groups */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ fontWeight: 900 }}>💪 Músculos</div>
            <button
              onClick={() => openMuscleModal(emptyMuscle())}
              style={primaryBtn}
            >
              ➕ Novo
            </button>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {sortedMuscles.map(m => (
              <div key={m.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', background: '#0f172a', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {m.imageUrl ? (
                    <img alt="muscle" src={m.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span style={{ color: '#fff', fontWeight: 900, opacity: 0.8 }}>—</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: '#0f172a' }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>ordem: {m.sortOrder ?? 0} • {m.isActive ? 'ATIVO' : 'INATIVO'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openMuscleModal({ id: m.id, name: m.name || '', imageUrl: m.imageUrl || '', muscleIds: (m.muscleId || '').split(',').map(s => s.trim()).filter(Boolean), sortOrder: m.sortOrder || 0, isActive: !!m.isActive })} style={secondaryBtn}>Editar</button>
                  <button onClick={() => deleteMuscle(m.id)} style={dangerBtn}>Eliminar</button>
                </div>
              </div>
            ))}
            {sortedMuscles.length === 0 && <div style={{ padding: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Sem músculos.</div>}
          </div>
        </div>

        {/* Equipment types */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ fontWeight: 900 }}>🏋️ Equipamentos</div>
            <button
              onClick={() => { setEquipmentForm(emptyEquipment()); setShowEquipmentModal(true); }}
              style={primaryBtn}
            >
              ➕ Novo
            </button>
          </div>

          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {sortedEquipment.map(eq => (
              <div key={eq.id} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', background: '#0f172a', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {eq.imageUrl ? (
                    <img alt="equip" src={eq.imageUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span style={{ color: '#fff', fontWeight: 900, opacity: 0.8 }}>—</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 900, color: '#0f172a' }}>{eq.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>ordem: {eq.sortOrder ?? 0} • {eq.isActive ? 'ATIVO' : 'INATIVO'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setEquipmentForm({ id: eq.id, name: eq.name || '', imageUrl: eq.imageUrl || '', sortOrder: eq.sortOrder || 0, isActive: !!eq.isActive }); setShowEquipmentModal(true); }} style={secondaryBtn}>Editar</button>
                  <button onClick={() => deleteEquipment(eq.id)} style={dangerBtn}>Eliminar</button>
                </div>
              </div>
            ))}
            {sortedEquipment.length === 0 && <div style={{ padding: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Sem equipamentos.</div>}
          </div>
        </div>
      </div>

      {/* Muscle modal */}
      {showMuscleModal && (
        <div style={modalOverlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>{muscleForm.id ? '✏️ Editar músculo' : '➕ Novo músculo'}</div>
              <button onClick={() => setShowMuscleModal(false)} style={miniBtnDanger}>✖</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, marginTop: 12 }}>
              <div>
                <label style={label}>Nome</label>
                <input value={muscleForm.name} onChange={(e) => setMuscleForm(prev => ({ ...prev, name: e.target.value }))} style={input} placeholder="Peito" />
              </div>
              <div>
                <label style={label}>Ordem</label>
                <input type="number" value={muscleForm.sortOrder} onChange={(e) => setMuscleForm(prev => ({ ...prev, sortOrder: e.target.value }))} style={input} />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={label}>Imagem (opcional)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={muscleForm.imageUrl} onChange={(e) => setMuscleForm(prev => ({ ...prev, imageUrl: e.target.value }))} style={input} placeholder="URL ou upload" />
                <input ref={muscleFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const url = await uploadToFolder(f, 'exercise-taxonomy');
                    setMuscleForm(prev => ({ ...prev, imageUrl: url || prev.imageUrl }));
                    toast.success('Imagem enviada ✅');
                  } catch {
                    toast.error('Erro no upload');
                  } finally {
                    e.target.value = '';
                  }
                }} />
                <button onClick={() => muscleFileRef.current?.click()} style={secondaryBtn}>📤</button>
                <button onClick={() => openAiImageSearch('muscle')} style={secondaryBtn} disabled={aiImageLoading}>🧠</button>
              </div>

              <div style={{ marginTop: 10, padding: 10, border: '1px solid #e5e7eb', borderRadius: 12, background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontWeight: 900, color: '#0f172a' }}>🧍 Boneco (clicar no músculo)</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>Clica numa zona para ficar a vermelho e depois “Usar imagem”.</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => setMuscleBodyView('front')} style={muscleBodyView === 'front' ? primaryBtn : secondaryBtn}>Frente</button>
                    <button onClick={() => setMuscleBodyView('back')} style={muscleBodyView === 'back' ? primaryBtn : secondaryBtn}>Costas</button>
                      <input
                        ref={lineArtFrontInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const url = await uploadToFolder(f, 'exercise-taxonomy');
                            if (url) {
                              persistLineArtToStorage(url, localStorage.getItem('exerciseTaxonomyLineArtBackUrl') || '');
                              setLineArtFrontTransform({ x: 0, y: 0, scale: 1 });
                              persistLineArtTransformToStorage({ x: 0, y: 0, scale: 1 }, lineArtBackTransform);
                              const data = await fetchAsDataUrl(url);
                              if (data) setLineArtFrontDataUrl(data);
                              toast.success('Desenho (normal) frente atualizado ✅');
                            }
                          } catch {
                            toast.error('Erro no upload');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                      <input
                        ref={lineArtBackInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const url = await uploadToFolder(f, 'exercise-taxonomy');
                            if (url) {
                              persistLineArtToStorage(localStorage.getItem('exerciseTaxonomyLineArtFrontUrl') || '', url);
                              setLineArtBackTransform({ x: 0, y: 0, scale: 1 });
                              persistLineArtTransformToStorage(lineArtFrontTransform, { x: 0, y: 0, scale: 1 });
                              const data = await fetchAsDataUrl(url);
                              if (data) setLineArtBackDataUrl(data);
                              toast.success('Desenho (normal) costas atualizado ✅');
                            }
                          } catch {
                            toast.error('Erro no upload');
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        onClick={() => (muscleBodyView === 'front' ? lineArtFrontInputRef.current?.click() : lineArtBackInputRef.current?.click())}
                        style={secondaryBtn}
                        title="Upload do desenho a linhas (modo normal). Não é o modo Realista."
                      >
                        ✍️ Desenho
                      </button>
                    <button
                      onClick={() => {
                        const next = muscleBodyMode === 'image' ? 'silhouette' : 'image';
                        setMuscleBodyMode(next);
                        if (next === 'image') loadBodyBaseFromStorage();
                      }}
                      style={muscleBodyMode === 'image' ? primaryBtn : secondaryBtn}
                      title="Usa uma imagem base (frente/costas) por baixo e pinta a zona selecionada"
                    >
                      {muscleBodyMode === 'image' ? '✅ Realista' : '🖼️ Realista'}
                    </button>
                    <button
                      onClick={() => {
                        if (muscleBodyMode === 'image') {
                          // Modo Realista (compat): usa a imagem base + zona antiga.
                          if (!muscleBodyRegion) { toast.error('Seleciona uma zona no boneco'); return; }
                          setMuscleForm(prev => ({ ...prev, imageUrl: bodyMapUrl || prev.imageUrl }));
                          toast.success('Imagem aplicada ✅');
                          return;
                        }
                        // Modelo anatómico: a fonte de verdade é o muscleIds; imageUrl é só preview.
                        if (!(muscleForm.muscleIds || []).length) { toast.error('Clica num músculo no modelo'); return; }
                        const preview = buildMuscleImageDataUrl(muscleForm.muscleIds, muscleBodyView);
                        setMuscleForm(prev => ({ ...prev, imageUrl: preview || prev.imageUrl }));
                        toast.success('Músculos aplicados ✅');
                      }}
                      style={primaryBtn}
                    >
                      Usar imagem
                    </button>
                  </div>
                </div>

                {muscleBodyMode === 'silhouette' && !!String((muscleBodyView === 'front' ? lineArtFrontDataUrl : lineArtBackDataUrl) || '').trim() && (
                  <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>X</div>
                      <input
                        type="number"
                        value={(muscleBodyView === 'front' ? lineArtFrontTransform.x : lineArtBackTransform.x)}
                        onChange={(e) => {
                          const v = Number(e.target.value || 0);
                          const t = (muscleBodyView === 'front' ? lineArtFrontTransform : lineArtBackTransform);
                          setActiveLineArtTransform({ ...t, x: v });
                        }}
                        style={input}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Y</div>
                      <input
                        type="number"
                        value={(muscleBodyView === 'front' ? lineArtFrontTransform.y : lineArtBackTransform.y)}
                        onChange={(e) => {
                          const v = Number(e.target.value || 0);
                          const t = (muscleBodyView === 'front' ? lineArtFrontTransform : lineArtBackTransform);
                          setActiveLineArtTransform({ ...t, y: v });
                        }}
                        style={input}
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Zoom</div>
                      <input
                        type="number"
                        step="0.01"
                        value={(muscleBodyView === 'front' ? lineArtFrontTransform.scale : lineArtBackTransform.scale)}
                        onChange={(e) => {
                          const v = clamp(Number(e.target.value || 1), 0.4, 3);
                          const t = (muscleBodyView === 'front' ? lineArtFrontTransform : lineArtBackTransform);
                          setActiveLineArtTransform({ ...t, scale: v });
                        }}
                        style={input}
                      />
                    </div>
                    <button onClick={() => setActiveLineArtTransform({ x: 0, y: 0, scale: 1 })} style={secondaryBtn}>Reset</button>
                    <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#64748b' }}>Dica: arrasta no boneco para posicionar; roda o rato para zoom.</div>
                  </div>
                )}

                {muscleBodyMode === 'image' && (
                  <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 6 }}>Imagem base (Frente)</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={muscleBodyBaseFrontUrl} onChange={(e) => {
                          const v = e.target.value;
                          setMuscleBodyBaseFrontUrl(v);
                          persistBodyBaseToStorage(v, muscleBodyBaseBackUrl);
                          scheduleSaveBodyMapToServer({ frontUrl: v });
                        }} style={input} placeholder="URL /uploads/..." />
                        <input type="file" accept="image/*" style={{ display: 'none' }} id="bodyBaseFrontUpload" onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const url = await uploadToFolder(f, 'exercise-taxonomy');
                            if (url) {
                              setMuscleBodyBaseFrontUrl(url);
                              persistBodyBaseToStorage(url, muscleBodyBaseBackUrl);
                              scheduleSaveBodyMapToServer({ frontUrl: url });
                              toast.success('Imagem base (frente) enviada ✅');
                            }
                          } catch {
                            toast.error('Erro no upload');
                          } finally {
                            e.target.value = '';
                          }
                        }} />
                        <button onClick={() => document.getElementById('bodyBaseFrontUpload')?.click()} style={secondaryBtn}>📤</button>
                      </div>

                      <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>X</div>
                          <input type="number" value={muscleBodyBaseFrontTransform.x} onChange={(e) => {
                            const next = { ...muscleBodyBaseFrontTransform, x: Number(e.target.value || 0) };
                            setMuscleBodyBaseFrontTransform(next);
                            persistBodyTransformToStorage(next, muscleBodyBaseBackTransform);
                            scheduleSaveBodyMapToServer({ frontX: next.x, frontY: next.y, frontScale: next.scale });
                          }} style={input} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Y</div>
                          <input type="number" value={muscleBodyBaseFrontTransform.y} onChange={(e) => {
                            const next = { ...muscleBodyBaseFrontTransform, y: Number(e.target.value || 0) };
                            setMuscleBodyBaseFrontTransform(next);
                            persistBodyTransformToStorage(next, muscleBodyBaseBackTransform);
                            scheduleSaveBodyMapToServer({ frontX: next.x, frontY: next.y, frontScale: next.scale });
                          }} style={input} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Zoom</div>
                          <input type="number" step="0.01" value={muscleBodyBaseFrontTransform.scale} onChange={(e) => {
                            const next = { ...muscleBodyBaseFrontTransform, scale: Number(e.target.value || 1) };
                            setMuscleBodyBaseFrontTransform(next);
                            persistBodyTransformToStorage(next, muscleBodyBaseBackTransform);
                            scheduleSaveBodyMapToServer({ frontX: next.x, frontY: next.y, frontScale: next.scale });
                          }} style={input} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 900, color: '#334155', marginBottom: 6 }}>Imagem base (Costas)</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={muscleBodyBaseBackUrl} onChange={(e) => {
                          const v = e.target.value;
                          setMuscleBodyBaseBackUrl(v);
                          persistBodyBaseToStorage(muscleBodyBaseFrontUrl, v);
                          scheduleSaveBodyMapToServer({ backUrl: v });
                        }} style={input} placeholder="URL /uploads/..." />
                        <input type="file" accept="image/*" style={{ display: 'none' }} id="bodyBaseBackUpload" onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          try {
                            const url = await uploadToFolder(f, 'exercise-taxonomy');
                            if (url) {
                              setMuscleBodyBaseBackUrl(url);
                              persistBodyBaseToStorage(muscleBodyBaseFrontUrl, url);
                              scheduleSaveBodyMapToServer({ backUrl: url });
                              toast.success('Imagem base (costas) enviada ✅');
                            }
                          } catch {
                            toast.error('Erro no upload');
                          } finally {
                            e.target.value = '';
                          }
                        }} />
                        <button onClick={() => document.getElementById('bodyBaseBackUpload')?.click()} style={secondaryBtn}>📤</button>
                      </div>

                      <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>X</div>
                          <input type="number" value={muscleBodyBaseBackTransform.x} onChange={(e) => {
                            const next = { ...muscleBodyBaseBackTransform, x: Number(e.target.value || 0) };
                            setMuscleBodyBaseBackTransform(next);
                            persistBodyTransformToStorage(muscleBodyBaseFrontTransform, next);
                            scheduleSaveBodyMapToServer({ backX: next.x, backY: next.y, backScale: next.scale });
                          }} style={input} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Y</div>
                          <input type="number" value={muscleBodyBaseBackTransform.y} onChange={(e) => {
                            const next = { ...muscleBodyBaseBackTransform, y: Number(e.target.value || 0) };
                            setMuscleBodyBaseBackTransform(next);
                            persistBodyTransformToStorage(muscleBodyBaseFrontTransform, next);
                            scheduleSaveBodyMapToServer({ backX: next.x, backY: next.y, backScale: next.scale });
                          }} style={input} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b', fontWeight: 900 }}>Zoom</div>
                          <input type="number" step="0.01" value={muscleBodyBaseBackTransform.scale} onChange={(e) => {
                            const next = { ...muscleBodyBaseBackTransform, scale: Number(e.target.value || 1) };
                            setMuscleBodyBaseBackTransform(next);
                            persistBodyTransformToStorage(muscleBodyBaseFrontTransform, next);
                            scheduleSaveBodyMapToServer({ backX: next.x, backY: next.y, backScale: next.scale });
                          }} style={input} />
                        </div>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#64748b' }}>
                      Para ficar igual ao exemplo que enviaste, faz upload das tuas imagens (frente e costas). Depois é só clicar no músculo e “Usar imagem”.
                    </div>

                    <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 12, fontWeight: 900, color: '#334155' }}>Opacidade</div>
                      <input
                        type="range"
                        min="0.15"
                        max="1"
                        step="0.01"
                        value={muscleBodyBaseOpacity}
                        onChange={(e) => {
                          const v = clamp(Number(e.target.value || 1), 0.15, 1);
                          setMuscleBodyBaseOpacity(v);
                          persistBodyOpacityToStorage(v);
                          scheduleSaveBodyMapToServer({ opacity: v });
                        }}
                        style={{ flex: 1, minWidth: 200 }}
                      />
                      <button onClick={() => {
                        setMuscleBodyBaseOpacity(1);
                        persistBodyOpacityToStorage(1);
                        scheduleSaveBodyMapToServer({ opacity: 1 });
                      }} style={secondaryBtn}>Reset</button>
                      <button onClick={() => {
                        if (muscleBodyView === 'front') {
                          const next = { x: 0, y: 0, scale: 1 };
                          setMuscleBodyBaseFrontTransform(next);
                          persistBodyTransformToStorage(next, muscleBodyBaseBackTransform);
                        } else {
                          const next = { x: 0, y: 0, scale: 1 };
                          setMuscleBodyBaseBackTransform(next);
                          persistBodyTransformToStorage(muscleBodyBaseFrontTransform, next);
                        }
                        toast.success('Ajuste reposto ✅');
                      }} style={secondaryBtn}>Reset ajuste</button>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Dica: arrasta no boneco para posicionar; roda o rato para zoom.</div>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12, alignItems: 'start' }}>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', overflow: 'hidden', padding: 8 }}>
                    {muscleBodyMode === 'image' && !!(muscleBodyView === 'front' ? muscleBodyBaseFrontUrl : muscleBodyBaseBackUrl) ? (
                      /* Modo "Realista" (compatibilidade): imagem base + destaque antigo por zona. */
                      <img alt="body-map" src={bodyMapUrl} style={{ width: '100%', height: 420, objectFit: 'contain', display: 'block' }} />
                    ) : (
                      /* Modelo anatómico principal — SVG interativo (MuscleMap). */
                      <MuscleMap
                        view={muscleBodyView}
                        selectedMuscleIds={muscleForm.muscleIds || []}
                        onMuscleToggle={onMuscleToggle}
                        height={440}
                      />
                    )}
                  </div>

                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <div style={{ padding: 10 }}>
                      <div style={{ fontSize: 12, color: '#64748b' }}>Selecionados ({selectedMuscleNames.length}):</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, minHeight: 22 }}>
                        {selectedMuscleNames.length === 0 ? (
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>
                        ) : (
                          (muscleForm.muscleIds || []).map((id) => (
                            <span key={id} onClick={() => onMuscleToggle({ muscleId: id })}
                              title="Clica para remover"
                              style={{ cursor: 'pointer', background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                              {MUSCLE_BY_ID[id]?.name || id} ✕
                            </span>
                          ))
                        )}
                      </div>
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={() => { setMuscleForm(prev => ({ ...prev, muscleIds: [] })); setMuscleBodyRegion(''); }} style={secondaryBtn}>Limpar</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {(muscleForm.imageUrl || '').trim() && (
                <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#0f172a' }}>
                  <img alt="preview" src={muscleForm.imageUrl} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 900, color: '#0f172a' }}>
                <input type="checkbox" checked={!!muscleForm.isActive} onChange={(e) => setMuscleForm(prev => ({ ...prev, isActive: e.target.checked }))} />
                Ativo
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setShowMuscleModal(false)} style={dangerBtn}>Cancelar</button>
              <button onClick={saveMuscle} style={primaryBtn}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Equipment modal */}
      {showEquipmentModal && (
        <div style={modalOverlay}>
          <div style={modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>{equipmentForm.id ? '✏️ Editar equipamento' : '➕ Novo equipamento'}</div>
              <button onClick={() => setShowEquipmentModal(false)} style={miniBtnDanger}>✖</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12, marginTop: 12 }}>
              <div>
                <label style={label}>Nome</label>
                <input value={equipmentForm.name} onChange={(e) => setEquipmentForm(prev => ({ ...prev, name: e.target.value }))} style={input} placeholder="Halteres" />
              </div>
              <div>
                <label style={label}>Ordem</label>
                <input type="number" value={equipmentForm.sortOrder} onChange={(e) => setEquipmentForm(prev => ({ ...prev, sortOrder: e.target.value }))} style={input} />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={label}>Imagem (opcional)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={equipmentForm.imageUrl} onChange={(e) => setEquipmentForm(prev => ({ ...prev, imageUrl: e.target.value }))} style={input} placeholder="URL ou upload" />
                <input ref={equipFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const url = await uploadToFolder(f, 'exercise-taxonomy');
                    setEquipmentForm(prev => ({ ...prev, imageUrl: url || prev.imageUrl }));
                    toast.success('Imagem enviada ✅');
                  } catch {
                    toast.error('Erro no upload');
                  } finally {
                    e.target.value = '';
                  }
                }} />
                <button onClick={() => equipFileRef.current?.click()} style={secondaryBtn}>📤</button>
                <button onClick={() => openAiImageSearch('equipment')} style={secondaryBtn} disabled={aiImageLoading}>🧠</button>
              </div>

              {(equipmentForm.imageUrl || '').trim() && (
                <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#0f172a' }}>
                  <img alt="preview" src={equipmentForm.imageUrl} style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </div>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 900, color: '#0f172a' }}>
                <input type="checkbox" checked={!!equipmentForm.isActive} onChange={(e) => setEquipmentForm(prev => ({ ...prev, isActive: e.target.checked }))} />
                Ativo
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setShowEquipmentModal(false)} style={dangerBtn}>Cancelar</button>
              <button onClick={saveEquipment} style={primaryBtn}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* AI image suggestions modal */}
      {showAiImageModal && (
        <div style={modalOverlay}>
          <div style={{ ...modal, maxWidth: 980 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 900 }}>🧠 Procurar imagem</div>
              <button onClick={() => setShowAiImageModal(false)} style={miniBtnDanger}>✖</button>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: '#64748b' }}>
              Query: <span style={{ fontWeight: 900, color: '#0f172a' }}>{aiImageQuery || '—'}</span>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => openAiImageSearch(aiImageTarget)} style={secondaryBtn} disabled={aiImageLoading}>🔄 Atualizar</button>
              <button onClick={() => setShowAiImageModal(false)} style={dangerBtn}>Fechar</button>
            </div>

            <div style={{ marginTop: 12 }}>
              {aiImageLoading && (
                <div style={{ padding: 14, textAlign: 'center', color: '#64748b' }}>A procurar imagens…</div>
              )}

              {!aiImageLoading && aiImageSuggestions.length === 0 && (
                <div style={{ padding: 14, textAlign: 'center', color: '#64748b' }}>Sem sugestões (verifica a configuração do Serper/Google).</div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {aiImageSuggestions.map((img, idx) => {
                  const { full, thumb } = pickAiImageUrls(img);
                  return (
                    <div key={`${idx}-${thumb}`} style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                      <div style={{ height: 140, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {!!thumb ? (
                          <img alt="suggestion" src={thumb} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <div style={{ color: '#fff', opacity: 0.8 }}>Sem preview</div>
                        )}
                      </div>
                      <div style={{ padding: 10, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={full || ''}>
                          {full ? full : '—'}
                        </div>
                        <button onClick={() => applyAiImage(full || thumb)} style={primaryBtn}>Usar</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  padding: 14,
};

const label = {
  display: 'block',
  fontSize: 12,
  fontWeight: 900,
  color: '#334155',
  marginBottom: 6,
};

const input = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #e5e7eb',
  outline: 'none',
  fontSize: 13,
  boxSizing: 'border-box',
};

const primaryBtn = {
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const secondaryBtn = {
  background: '#fff',
  color: '#0f172a',
  border: '1px solid #e5e7eb',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const dangerBtn = {
  background: '#ef4444',
  color: '#fff',
  border: 'none',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const miniBtnDanger = {
  background: '#ef4444',
  color: '#fff',
  border: 'none',
  width: 34,
  height: 34,
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 900,
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2000,
  padding: 14,
};

const modal = {
  background: '#fff',
  width: 'min(720px, 98vw)',
  borderRadius: 16,
  padding: 14,
  border: '1px solid #e5e7eb',
  boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
  maxHeight: '92vh',
  overflow: 'auto',
};

export default AdminExerciseTaxonomyPage;
